/**
 * Local Storage Service
 * Save learning progress locally, no server needed
 */

export interface UserProgress {
  id: string;
  name: string;
  age: number;
  avatar: string;
  level: number; // 1-10
  points: number;
  badges: string[];
  wordsLearned: string[];
  lessonsCompleted: string[];
  streakDays: number;
  lastPracticeDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface LearningRecord {
  id: string;
  userId: string;
  type: 'vocabulary' | 'listening' | 'speaking' | 'conversation';
  contentId: string;
  score: number;
  duration: number; // seconds
  createdAt: string;
}

export interface WordProgress {
  word: string;
  mastery: number; // 0-5, how well mastered
  lastPracticed: string;
  practiceCount: number;
}

const STORAGE_KEYS = {
  USERS: 'english_tutor_users',
  CURRENT_USER: 'english_tutor_current_user',
  LEARNING_RECORDS: 'english_tutor_learning_records',
  WORD_PROGRESS: 'english_tutor_word_progress',
  SETTINGS: 'english_tutor_settings',
};

export class StorageService {
  // Generate unique ID
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Save data to localStorage
  private save(key: string, data: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  // Load data from localStorage
  private load<T>(key: string): T | null {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return null;
    }
  }

  // User Management
  // ===============

  // Create new user
  createUser(name: string, age: number, avatar: string): UserProgress {
    const user: UserProgress = {
      id: this.generateId(),
      name,
      age,
      avatar,
      level: 1,
      points: 0,
      badges: [],
      wordsLearned: [],
      lessonsCompleted: [],
      streakDays: 0,
      lastPracticeDate: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add to users list
    const users = this.getAllUsers();
    users.push(user);
    this.save(STORAGE_KEYS.USERS, users);

    // Set as current user
    this.setCurrentUser(user.id);

    return user;
  }

  // Get all users
  getAllUsers(): UserProgress[] {
    return this.load<UserProgress[]>(STORAGE_KEYS.USERS) || [];
  }

  // Get user by ID
  getUser(userId: string): UserProgress | null {
    const users = this.getAllUsers();
    return users.find(u => u.id === userId) || null;
  }

  // Set current user
  setCurrentUser(userId: string): void {
    this.save(STORAGE_KEYS.CURRENT_USER, userId);
  }

  // Get current user
  getCurrentUser(): UserProgress | null {
    const userId = this.load<string>(STORAGE_KEYS.CURRENT_USER);
    if (!userId) return null;
    return this.getUser(userId);
  }

  // Update user
  updateUser(userId: string, updates: Partial<UserProgress>): UserProgress | null {
    const users = this.getAllUsers();
    const index = users.findIndex(u => u.id === userId);

    if (index === -1) return null;

    users[index] = {
      ...users[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    this.save(STORAGE_KEYS.USERS, users);
    return users[index];
  }

  // Delete user
  deleteUser(userId: string): boolean {
    const users = this.getAllUsers();
    const filtered = users.filter(u => u.id !== userId);

    if (filtered.length === users.length) return false;

    this.save(STORAGE_KEYS.USERS, filtered);

    // If deleted user was current, clear current user
    if (this.getCurrentUser()?.id === userId) {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    }

    return true;
  }

  // Learning Records
  // =================

  // Add learning record
  addLearningRecord(userId: string, type: string, contentId: string, score: number, duration: number): LearningRecord {
    const record: LearningRecord = {
      id: this.generateId(),
      userId,
      type: type as any,
      contentId,
      score,
      duration,
      createdAt: new Date().toISOString(),
    };

    const records = this.getAllLearningRecords();
    records.push(record);
    this.save(STORAGE_KEYS.LEARNING_RECORDS, records);

    return record;
  }

  // Get all learning records
  getAllLearningRecords(): LearningRecord[] {
    return this.load<LearningRecord[]>(STORAGE_KEYS.LEARNING_RECORDS) || [];
  }

  // Get user's learning records
  getUserLearningRecords(userId: string): LearningRecord[] {
    return this.getAllLearningRecords().filter(r => r.userId === userId);
  }

  // Word Progress
  // ==============

  // Update word progress
  updateWordProgress(userId: string, word: string, masteryChange: number): WordProgress {
    const allProgress = this.getAllWordProgress();
    const key = `${userId}-${word}`;

    const existing = allProgress[key];

    if (existing) {
      existing.mastery = Math.min(5, Math.max(0, existing.mastery + masteryChange));
      existing.lastPracticed = new Date().toISOString();
      existing.practiceCount++;
    } else {
      allProgress[key] = {
        word,
        mastery: Math.min(5, Math.max(0, masteryChange)),
        lastPracticed: new Date().toISOString(),
        practiceCount: 1,
      };
    }

    this.save(STORAGE_KEYS.WORD_PROGRESS, allProgress);
    return allProgress[key];
  }

  // Get all word progress
  getAllWordProgress(): Record<string, WordProgress> {
    return this.load<Record<string, WordProgress>>(STORAGE_KEYS.WORD_PROGRESS) || {};
  }

  // Get user's word progress
  getUserWordProgress(userId: string): WordProgress[] {
    const allProgress = this.getAllWordProgress();
    return Object.values(allProgress).filter(wp =>
      wp.word.startsWith(userId)
    ).map(wp => ({
      ...wp,
      word: wp.word.replace(`${userId}-`, ''),
    }));
  }

  // Points & Badges
  // ================

  // Add points to user
  addPoints(userId: string, points: number): UserProgress | null {
    const user = this.getUser(userId);
    if (!user) return null;

    user.points += points;

    // Check for level up
    const pointsPerLevel = 100;
    const newLevel = Math.floor(user.points / pointsPerLevel) + 1;

    if (newLevel > user.level) {
      user.level = newLevel;
      // Could add badge for level up
    }

    return this.updateUser(userId, user);
  }

  // Add badge to user
  addBadge(userId: string, badge: string): UserProgress | null {
    const user = this.getUser(userId);
    if (!user) return null;

    if (!user.badges.includes(badge)) {
      user.badges.push(badge);
      return this.updateUser(userId, user);
    }

    return user;
  }

  // Settings
  // =========

  // Get settings
  getSettings(): Record<string, any> {
    return this.load<Record<string, any>>(STORAGE_KEYS.SETTINGS) || {};
  }

  // Update settings
  updateSettings(settings: Record<string, any>): void {
    const current = this.getSettings();
    this.save(STORAGE_KEYS.SETTINGS, { ...current, ...settings });
  }

  // Export all data (for backup)
  exportData(): string {
    const data = {
      users: this.getAllUsers(),
      records: this.getAllLearningRecords(),
      wordProgress: this.getAllWordProgress(),
      settings: this.getSettings(),
      currentUser: this.getCurrentUser()?.id,
    };

    return JSON.stringify(data, null, 2);
  }

  // Import data (for restore)
  importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);

      if (data.users) this.save(STORAGE_KEYS.USERS, data.users);
      if (data.records) this.save(STORAGE_KEYS.LEARNING_RECORDS, data.records);
      if (data.wordProgress) this.save(STORAGE_KEYS.WORD_PROGRESS, data.wordProgress);
      if (data.settings) this.save(STORAGE_KEYS.SETTINGS, data.settings);
      if (data.currentUser) this.save(STORAGE_KEYS.CURRENT_USER, data.currentUser);

      return true;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }

  // Clear all data
  clearAllData(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
}

// Create singleton instance
export const storageService = new StorageService();